"use client";

import staticData from "@/public/data/flight/results.json";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/shared/components/common/carousel";
import Flex from "@/shared/components/common/Flex";
import List from "@/shared/components/common/List";
import Typography from "@/shared/components/common/Typography";
import { useDeviceType } from "@/shared/context/DeviceContext";
import { cn } from "@/shared/libs/commonUtils";
import type { CarouselApi } from "@/types/components/carousel";
import type { PriceCalendarEntry } from "@/types/services/flight/apis/Availability";
import Link from "next/link";
import { usePathname, useSearchParams } from "next/navigation";
import { useEffect, useState } from "react";
import { createCalendarDate, formatPriceCalendarDate } from "./utils";
import config from "@/configs/config";

interface PriceCalendarProps {
  priceCalendarData: PriceCalendarEntry[];
}

export default function PriceCalendar({
  priceCalendarData,
}: PriceCalendarProps) {
  const pathName = usePathname();
  const params = useSearchParams();
  const deviceType = useDeviceType();

  const selectedIndex = priceCalendarData.findIndex(
    ({ date }) => date === params.get("depart"),
  );

  const [emblaApi, setEmblaApi] = useState<CarouselApi>();

  const isSelected = (index: number) => index === selectedIndex;

  useEffect(() => {
    if (emblaApi && selectedIndex !== -1) {
      emblaApi.scrollTo(selectedIndex - 2, false);
    }
  }, [emblaApi, selectedIndex]);

  return (
    <Carousel
      opts={{
        dragFree: true,
        duration: 20,
        startIndex: selectedIndex !== -1 ? selectedIndex : 0,
        align: "center",
        containScroll: "trimSnaps",
        direction: config.dir,
      }}
      setApi={setEmblaApi}
    >
      <Flex
        alignItems={"center"}
        className="bg-background h-14 rounded-md ring-1 ring-border px-3"
      >
        <CarouselNext
          size={"sm"}
          className="!static !translate-x-0 !translate-y-0 min-w-10 min-h-10"
        />
        <CarouselContent>
          <List
            items={priceCalendarData}
            renderItems={({ date, price }, index) => (
              <CarouselItem
                className={cn(
                  "basis-1/4 sm:basis-1/3 md:basis-1/5 cursor-pointer !p-0 select-none h-14 relative",

                  index !== priceCalendarData.length - 1 &&
                    index !== selectedIndex &&
                    index !== selectedIndex
                    ? "before:w-[1px] before:h-1/2 before:absolute before:left-0 before:top-0 before:bottom-0 before:bg-grayColor before:translate-y-1/2"
                    : "",
                  isSelected(index)
                    ? "bg-muted/80 dark:bg-muted/10 rounded-sm"
                    : "",
                )}
              >
                <Link href={createCalendarDate(date, pathName, params)}>
                  <Flex
                    direction={"col"}
                    alignItems={"center"}
                    justifyContent={"center"}
                    gap={"1"}
                    className={"h-full"}
                  >
                    <Typography
                      variant={"caption"}
                      className={cn(
                        "text-grayColor-foreground max-md:text-[10px]",
                        isSelected(index) ? "text-link font-semibold" : "",
                      )}
                    >
                      {formatPriceCalendarDate(deviceType, date)}
                    </Typography>
                    {price && (
                      <Typography
                        variant={"body3"}
                        className={cn(
                          "text-foreground max-md:text-xs",
                          isSelected(index) ? "font-semibold" : "",
                        )}
                      >
                        {price}
                      </Typography>
                    )}
                    {!price && (
                      <Typography
                        variant={"caption"}
                        className="text-grayColor-foreground"
                      >
                        {deviceType === "mobile"
                          ? staticData.priceCalendar.bookedLabelMobile
                          : staticData.priceCalendar.bookedLabel}
                      </Typography>
                    )}
                  </Flex>
                </Link>
              </CarouselItem>
            )}
          />
        </CarouselContent>

        <CarouselPrevious
          variant={"ghost"}
          size={"sm"}
          className="!static !translate-x-0 !translate-y-0 min-w-10 min-h-10"
        />
      </Flex>
    </Carousel>
  );
}
